import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@loot-os/ui/components/command";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Building2, CornerDownRight, Package, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { buildCommandIndex, filterCommands, groupCommands } from "../lib/commandIndex";
import { setEnvironmentForTenant } from "../lib/environment";
import { PLATFORM_TOOLS } from "../lib/platformTools";
import { TENANT_NAV } from "../lib/tenantNav";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { useTenantRole } from "../lib/useTenantRole";

/** Mirrors the input's minimum on `platform.search.quick`: shorter queries stay local. */
const MIN_SEARCH_LENGTH = 2;

/** Where a search result lives; live clears a remembered sandbox, as the switcher does. */
interface ScopedResult {
  tenantId: string;
  environmentId: string;
  environmentType: "live" | "sandbox";
}

/**
 * The ⌘K palette: navigation from the two registries and the caller's tenants (filtered
 * here), plus, for a platform admin, `platform.search.quick` over every tenant's shoppers
 * and boxes. Mounted once in `AppShell`; the shortcut is `useCommandPaletteHotkey`.
 * cmdk's own filtering is off because the server already chose its rows, and its vim
 * bindings are off because they put Ctrl+K on "previous item".
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };
  const { isPlatformAdmin, tenants, role, query: me } = useTenantRole();
  const [value, setValue] = useState("");
  const query = value.trim();
  const debounced = useDebounced(query);

  // Every close path (Escape, backdrop, the shortcut, a pick) reopens on a cleared query.
  useEffect(() => {
    if (!open) setValue("");
  }, [open]);

  const index = useMemo(
    () =>
      buildCommandIndex({
        platformTools: PLATFORM_TOOLS,
        tenantNav: TENANT_NAV,
        tenants,
        tenantId: tenantId ?? null,
        isPlatformAdmin,
        role,
      }),
    [tenants, tenantId, isPlatformAdmin, role],
  );
  const groups = useMemo(
    () => groupCommands(filterCommands(index, value, { hideTenantsOnEmpty: isPlatformAdmin })),
    [index, value, isPlatformAdmin],
  );

  const searching = isPlatformAdmin && query.length >= MIN_SEARCH_LENGTH;
  const results = useQuery(
    trpc.platform.search.quick.queryOptions(
      { query: debounced, limit: 5 },
      {
        enabled: open && searching && debounced.length >= MIN_SEARCH_LENGTH,
        // Keep the last rows on screen while the next query resolves.
        placeholderData: keepPreviousData,
      },
    ),
  );
  // Gate on the live query, not the data: a disabled query still holds its placeholder.
  const endUsers = searching ? (results.data?.endUsers ?? []) : [];
  const boxes = searching ? (results.data?.boxes ?? []) : [];
  const pending = searching && (debounced !== query || results.isFetching);

  function go(href: string) {
    // Close before navigating: a navigate racing the popup's close can leave the body's
    // scroll lock behind.
    onOpenChange(false);
    void navigate({ href });
  }

  function goScoped(result: ScopedResult, href: string) {
    // The page's first queries read the environment header from the scope store, so the
    // selection has to land before the tenant route's beforeLoad runs.
    setEnvironmentForTenant(
      result.tenantId,
      result.environmentType === "live" ? null : result.environmentId,
    );
    go(href);
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Jump to a page, a tenant, a shopper or a box."
      className="sm:max-w-xl"
    >
      <Command shouldFilter={false} loop vimBindings={false} label="Search">
        <CommandInput
          value={value}
          onValueChange={setValue}
          placeholder={
            isPlatformAdmin
              ? "Search pages, tenants, shoppers and boxes…"
              : "Search pages and tenants…"
          }
        />
        <CommandList>
          <CommandEmpty>
            {pending
              ? "Searching…"
              : query
                ? "No results."
                : // The index is built from me.tenants: until it resolves there is nothing to list.
                  me.isPending
                  ? "Loading…"
                  : "Type to search…"}
          </CommandEmpty>

          {groups.map((group) => (
            <CommandGroup key={group.group} heading={group.group}>
              {group.items.map((target) => {
                const Icon =
                  target.icon ?? (target.group === "Tenants" ? Building2 : CornerDownRight);
                return (
                  <CommandItem key={target.id} value={target.id} onSelect={() => go(target.href)}>
                    <Icon className="text-muted-foreground" />
                    <span className="truncate">{target.label}</span>
                    {target.description && (
                      // Sized from zero (`flex-1`), the description fills only what the name
                      // leaves, so a long one truncates and the name never gives way.
                      <span className="min-w-0 flex-1 truncate text-muted-foreground text-xs">
                        {target.description}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}

          {endUsers.length > 0 && (
            <>
              {groups.length > 0 && <CommandSeparator />}
              <CommandGroup heading="Shoppers">
                {endUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`user:${user.id}`}
                    onSelect={() => goScoped(user, `/tenants/${user.tenantId}/users/${user.id}`)}
                  >
                    <User className="text-muted-foreground" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{user.username ?? user.externalId}</span>
                      <span className="truncate text-muted-foreground text-xs">
                        {[
                          user.email,
                          user.tenantArchivedAt ? `${user.tenantName} (archived)` : user.tenantName,
                          user.environmentType === "sandbox" ? user.environmentName : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {boxes.length > 0 && (
            <>
              {(groups.length > 0 || endUsers.length > 0) && <CommandSeparator />}
              <CommandGroup heading="Boxes">
                {boxes.map((box) => (
                  <CommandItem
                    key={box.id}
                    value={`box:${box.id}`}
                    onSelect={() =>
                      box.tenantId && box.environmentId && box.environmentType
                        ? goScoped(
                            {
                              tenantId: box.tenantId,
                              environmentId: box.environmentId,
                              environmentType: box.environmentType,
                            },
                            `/tenants/${box.tenantId}/boxes/${box.id}`,
                          )
                        : go(`/templates/${box.id}`)
                    }
                  >
                    <Package className="text-muted-foreground" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{box.name}</span>
                      <span className="truncate text-muted-foreground text-xs">
                        {[
                          box.tenantArchivedAt
                            ? `${box.tenantName} (archived)`
                            : (box.tenantName ?? "Template"),
                          box.environmentType === "sandbox" ? box.environmentName : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
